/**
 * JourneyGraph — Interactive React Flow visualization of the Customer Journey.
 * Uses @xyflow/react (locally bundled, AIRGAP-compatible).
 * Every node is clickable and opens a side drawer with drill-through.
 * Left → Right layout following journey progression.
 */
import { useState, useCallback, useEffect } from "react";
import {
  ReactFlow, Background, Controls, MiniMap,
  useNodesState, useEdgesState, MarkerType,
} from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import {
  Target, Building2, UserCheck, TrendingUp, Calendar,
  FileText, Layers, GitBranch, AlertTriangle, ShieldCheck,
  AlertCircle, CheckCircle2, Activity, X, ArrowRight,
  Zap,
} from "lucide-react";

// ── Node type → visual config ─────────────────────────────────────────────
const NODE_CONFIG = {
  LEAD:           { icon: Target,        color: "#0176D3", bg: "#EEF4FF", border: "#A8D0F5", label: "Lead" },
  ACCOUNT:        { icon: Building2,     color: "#0176D3", bg: "#EEF4FF", border: "#A8D0F5", label: "Account" },
  CONTACT:        { icon: UserCheck,     color: "#0176D3", bg: "#EEF4FF", border: "#A8D0F5", label: "Contact" },
  OPPORTUNITY:    { icon: TrendingUp,    color: "#2E844A", bg: "#E8F4EE", border: "#A3C9A8", label: "Opportunity" },
  EVENT:          { icon: Calendar,      color: "#0176D3", bg: "#EEF4FF", border: "#A8D0F5", label: "Event" },
  EVENT_BRIEF:    { icon: FileText,      color: "#0176D3", bg: "#F3EFFE", border: "#C5A8F5", label: "Brief" },
  BLUEPRINT:      { icon: Layers,        color: "#9B59B6", bg: "#F3EFFE", border: "#C5A8F5", label: "Blueprint" },
  THREAD:         { icon: GitBranch,     color: "#0176D3", bg: "#EEF4FF", border: "#A8D0F5", label: "Thread" },
  CHANGE:         { icon: AlertTriangle, color: "#FE9339", bg: "#FEF4E6", border: "#FECB82", label: "Change" },
  APPROVAL:       { icon: ShieldCheck,   color: "#FE9339", bg: "#FEF4E6", border: "#FECB82", label: "Approval" },
  ISSUE:          { icon: AlertCircle,   color: "#BA0517", bg: "#FEE5E5", border: "#F5A3A3", label: "Issue" },
  MILESTONE:      { icon: CheckCircle2,  color: "#2E844A", bg: "#E8F4EE", border: "#A3C9A8", label: "Milestone" },
  READINESS_GATE: { icon: Zap,           color: "#0176D3", bg: "#EEF4FF", border: "#A8D0F5", label: "Event 360" },
  DEFAULT:        { icon: Activity,      color: "#706E6B", bg: "#F3F3F3", border: "#DDDBDA", label: "Node" },
};

function getStatusStyle(status) {
  if (!status) return {};
  const s = status.toUpperCase();
  if (["COMPLETED","READY","RESOLVED","APPROVED","ACTIVE"].some(x => s.includes(x)))
    return { borderColor: "#2E844A", background: "#E8F4EE" };
  if (["BLOCKED","CRITICAL","FAILED"].some(x => s.includes(x)))
    return { borderColor: "#BA0517", background: "#FEE5E5" };
  if (["AT_RISK","ATTENTION","PENDING","OPEN"].some(x => s.includes(x)))
    return { borderColor: "#FE9339", background: "#FEF4E6" };
  return {};
}

// Custom node component
function CustomNode({ data }) {
  const cfg = NODE_CONFIG[data.node_type] || NODE_CONFIG.DEFAULT;
  const Icon = cfg.icon;
  const statusStyle = getStatusStyle(data.status);

  return (
    <div
      data-testid={`graph-node-${data.node_id}`}
      onClick={data.onNodeClick}
      className="cursor-pointer transition-all hover:shadow-lg"
      style={{
        background: statusStyle.background || cfg.bg,
        border: `2px solid ${statusStyle.borderColor || cfg.border}`,
        borderRadius: "8px",
        padding: "8px 12px",
        minWidth: "140px",
        maxWidth: "180px",
        fontFamily: '"Segoe UI", Arial, sans-serif',
      }}
    >
      <div className="flex items-center gap-2">
        <div
          className="w-6 h-6 rounded flex items-center justify-center flex-shrink-0"
          style={{ background: cfg.color + "22" }}
        >
          <Icon className="w-3.5 h-3.5" style={{ color: cfg.color }} />
        </div>
        <div className="min-w-0">
          <p className="text-[9px] font-bold uppercase tracking-wider" style={{ color: cfg.color }}>
            {cfg.label}
          </p>
          <p className="text-xs font-medium text-[#181818] truncate leading-tight mt-0.5"
            style={{ maxWidth: "120px" }}>
            {data.label}
          </p>
        </div>
      </div>
      {data.owner && (
        <p className="text-[9px] text-[#706E6B] mt-1 truncate">{data.owner}</p>
      )}
    </div>
  );
}

const nodeTypes = { custom: CustomNode };

// ── Node Drawer — bottom-sheet on mobile, side-panel on desktop ────────────
function NodeDrawer({ node, onClose, onNavigate }) {
  if (!node) return null;
  const cfg = NODE_CONFIG[node.node_type] || NODE_CONFIG.DEFAULT;
  const Icon = cfg.icon;

  function fmtDate(iso) {
    if (!iso) return "";
    try { return new Date(iso).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" }); }
    catch { return iso; }
  }

  return (
    <>
      {/* Mobile bottom-sheet */}
      <div
        className="sm:hidden fixed inset-x-0 bottom-0 z-50 bg-white border-t border-[#E5E5E5] shadow-2xl flex flex-col rounded-t-2xl max-h-[65vh]"
        data-testid="node-drawer"
      >
        {/* Drag handle */}
        <div className="flex justify-center pt-2 pb-1">
          <div className="w-8 h-1 rounded-full bg-[#E5E5E5]" />
        </div>
        <div className="flex items-start justify-between px-4 pb-3 pt-1 border-b border-[#E5E5E5]">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded flex items-center justify-center" style={{ background: cfg.color + "22" }}>
              <Icon className="w-4 h-4" style={{ color: cfg.color }} />
            </div>
            <div>
              <p className="text-[10px] font-bold uppercase tracking-wider" style={{ color: cfg.color }}>{cfg.label}</p>
              <p className="text-sm font-semibold text-[#181818] leading-tight mt-0.5">{node.label}</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 -m-1 hover:bg-[#F3F3F3] rounded-full min-h-[44px] min-w-[44px] flex items-center justify-center">
            <X className="w-4 h-4 text-[#706E6B]" />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto p-4 space-y-3">
          {node.status && <div><p className="text-[10px] font-bold uppercase tracking-wider text-[#706E6B]">Status</p><p className="text-sm text-[#181818] mt-0.5">{node.status}</p></div>}
          {node.owner && <div><p className="text-[10px] font-bold uppercase tracking-wider text-[#706E6B]">Owner</p><p className="text-sm text-[#181818] mt-0.5">{node.owner}</p></div>}
          {node.timestamp && <div><p className="text-[10px] font-bold uppercase tracking-wider text-[#706E6B]">Date</p><p className="text-sm text-[#181818] mt-0.5">{fmtDate(node.timestamp)}</p></div>}
          {node.severity && <div><p className="text-[10px] font-bold uppercase tracking-wider text-[#706E6B]">Severity</p><p className="text-sm text-[#181818] mt-0.5">{node.severity}</p></div>}
          {node.source_id && <div><p className="text-[10px] font-bold uppercase tracking-wider text-[#706E6B]">Record ID</p><p className="text-xs text-[#706E6B] mt-0.5 font-mono break-all">{node.source_id}</p></div>}
        </div>
        {node.route && (
          <div className="p-4 border-t border-[#E5E5E5]">
            <button data-testid="node-open-record" onClick={() => onNavigate && onNavigate(node.route)}
              className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded text-white text-sm font-semibold transition-colors min-h-[44px]"
              style={{ background: "#0176D3" }}>
              Open Record <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        )}
      </div>
      {/* Desktop side panel */}
      <div
        className="hidden sm:flex absolute right-0 top-0 h-full w-72 bg-white border-l border-[#E5E5E5] shadow-xl z-50 flex-col"
        data-testid="node-drawer"
      >
        <div className="flex items-start justify-between p-4 border-b border-[#E5E5E5]">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded flex items-center justify-center" style={{ background: cfg.color + "22" }}>
              <Icon className="w-4 h-4" style={{ color: cfg.color }} />
            </div>
            <div>
              <p className="text-[10px] font-bold uppercase tracking-wider" style={{ color: cfg.color }}>{cfg.label}</p>
              <p className="text-sm font-semibold text-[#181818] leading-tight mt-0.5">{node.label}</p>
            </div>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-[#F3F3F3] rounded">
            <X className="w-4 h-4 text-[#706E6B]" />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto p-4 space-y-3">
          {node.status && <div><p className="text-[10px] font-bold uppercase tracking-wider text-[#706E6B]">Status</p><p className="text-sm text-[#181818] mt-0.5">{node.status}</p></div>}
          {node.owner && <div><p className="text-[10px] font-bold uppercase tracking-wider text-[#706E6B]">Owner</p><p className="text-sm text-[#181818] mt-0.5">{node.owner}</p></div>}
          {node.timestamp && <div><p className="text-[10px] font-bold uppercase tracking-wider text-[#706E6B]">Date</p><p className="text-sm text-[#181818] mt-0.5">{fmtDate(node.timestamp)}</p></div>}
          {node.severity && <div><p className="text-[10px] font-bold uppercase tracking-wider text-[#706E6B]">Severity</p><p className="text-sm text-[#181818] mt-0.5">{node.severity}</p></div>}
          {node.source_id && <div><p className="text-[10px] font-bold uppercase tracking-wider text-[#706E6B]">Record ID</p><p className="text-xs text-[#706E6B] mt-0.5 font-mono break-all">{node.source_id}</p></div>}
        </div>
        {node.route && (
          <div className="p-4 border-t border-[#E5E5E5]">
            <button data-testid="node-open-record" onClick={() => onNavigate && onNavigate(node.route)}
              className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded text-white text-sm font-semibold transition-colors"
              style={{ background: "#0176D3" }}>
              Open Record <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        )}
      </div>
    </>
  );
}

// ── Main JourneyGraph component ────────────────────────────────────────────
export function JourneyGraph({ graphData, loading, onNavigate }) {
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  const [selectedNode, setSelectedNode] = useState(null);

  useEffect(() => {
    if (!graphData) return;
    const rfNodes = (graphData.nodes || []).map((n) => ({
      id: n.node_id,
      type: "custom",
      position: n.position || { x: 0, y: 0 },
      data: {
        ...n,
        onNodeClick: () => setSelectedNode(n),
      },
    }));

    const rfEdges = (graphData.edges || []).map((e) => ({
      id: e.edge_id,
      source: e.source_node,
      target: e.target_node,
      label: e.relationship_type?.replace(/_/g, " "),
      type: "smoothstep",
      animated: false,
      style: { stroke: "#A8B7C7", strokeWidth: 1.5 },
      labelStyle: { fontSize: 9, fill: "#B0ADAB" },
      labelBgStyle: { fill: "transparent" },
      markerEnd: {
        type: MarkerType.ArrowClosed,
        width: 12,
        height: 12,
        color: "#A8B7C7",
      },
    }));

    setNodes(rfNodes);
    setEdges(rfEdges);
  }, [graphData]);

  if (loading) return (
    <div className="flex items-center justify-center h-full">
      <div className="animate-spin w-8 h-8 border-2 border-[#0176D3] border-t-transparent rounded-full" />
    </div>
  );

  if (!graphData || !graphData.nodes?.length) return (
    <div className="flex items-center justify-center h-full text-sm text-[#706E6B]">
      No journey graph data available
    </div>
  );

  return (
    <div className="relative w-full h-full" data-testid="journey-graph">
      <ReactFlow
        nodes={nodes}
        edges={edges}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        nodeTypes={nodeTypes}
        fitView
        fitViewOptions={{ padding: 0.15 }}
        minZoom={0.3}
        maxZoom={2}
        panOnDrag={[1, 2]}
        zoomOnPinch={true}
        zoomOnScroll={true}
        preventScrolling={true}
        attributionPosition="bottom-left"
      >
        <Background gap={16} size={1} color="#E5E5E5" />
        <Controls position="bottom-right" showInteractive={false} />
        {/* MiniMap — desktop only (wastes space on mobile) */}
        <div className="hidden sm:block">
          <MiniMap
            nodeColor={(n) => {
              const cfg = NODE_CONFIG[n.data?.node_type] || NODE_CONFIG.DEFAULT;
              return cfg.color;
            }}
            maskColor="rgba(255,255,255,0.6)"
            style={{ background: "#F3F3F3", border: "1px solid #E5E5E5" }}
          />
        </div>
      </ReactFlow>

      {/* Node drawer overlay */}
      <NodeDrawer
        node={selectedNode}
        onClose={() => setSelectedNode(null)}
        onNavigate={onNavigate}
      />
    </div>
  );
}

export default JourneyGraph;
